
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { CardContent } from '@/components/ui/card'
import { NodeContainer } from '../base/NodeContainer'
import { withNodeOptimization } from '../hoc/withNodeOptimization'
import { Icon } from '@/components/ui/icon'
import { Button } from '@/components/ui/button'
import { NumberInput } from '@/components/ui/number-input'
import { cn } from '@/lib/utils'
import {
  generateVariationsAsync,
  type AttributeCategory,
  type ConflictRule,
  type GeneratedVariation,
  type GenerationStats,
  type SelectedAttribute,
} from '@/lib/canvas/utils/variationGenerator'
import { useSeriesDataStore } from '@/stores/useSeriesDataStore'

/** Color class based on weight percentage tier from PRD */
function frequencyTierClass(attr: SelectedAttribute, categories: AttributeCategory[] | null): string {
  if (!categories || !attr.optionId) return ''
  const cat = categories.find((c) => c.id === attr.categoryId)
  if (!cat) return ''
  const opt = cat.options.find((o) => o.id === attr.optionId)
  if (!opt) return ''
  const total = cat.options.reduce((s, o) => s + o.weight, 0) + (cat.isRequired ? 0 : cat.noneWeight)
  if (total <= 0) return ''
  const pct = (opt.weight / total) * 100
  if (pct < 3) return 'text-amber-400'      // very low — gold
  if (pct < 10) return 'text-violet-400'     // low — purple
  if (pct < 20) return 'text-blue-400'       // medium — blue
  return ''                                   // high >20% — no highlight
}

// ── Props ───────────────────────────────────────────────────────────────────

interface VariationGeneratorProps {
  id: string
  title?: string
  selected?: boolean
  width?: number
  height?: number
  onSelect?: () => void
  onHoverChange?: (hovered: boolean) => void
  onStateChange?: (updates: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => void
  state?: Record<string, unknown>
  connectedCategories?: AttributeCategory[] | null
  connectedConflictRules?: ConflictRule[]
}

// ── Constants ───────────────────────────────────────────────────────────────

const ROW_HEIGHT = 26
const BUFFER_ROWS = 10

// ── Variation row ───────────────────────────────────────────────────────────

interface VariationRowProps {
  variation: GeneratedVariation
  categories: AttributeCategory[] | null
  style: React.CSSProperties
  onSwapAttribute: (variationIndex: number, categoryId: string, optionId: string) => void
  onLock: (variationIndex: number) => void
  onDelete: (variationIndex: number) => void
  onDuplicate: (variationIndex: number) => void
}

const VariationRow = React.memo(function VariationRow({
  variation, categories, style, onSwapAttribute, onLock, onDelete, onDuplicate,
}: VariationRowProps) {
  const summary = variation.attributes
    .filter((a) => a.optionName !== null)
    .map((a) => a.optionName)
    .join(' · ')

  return (
    <div
      className="group flex items-center gap-1 px-2 text-xs border-b border-border/30 hover:bg-muted/30"
      style={{ ...style, height: ROW_HEIGHT, overflow: 'hidden' }}
    >
      <span className="text-[10px] text-muted-foreground tabular-nums w-5 shrink-0 text-right">
        #{variation.index}
      </span>
      {variation.locked && (
        <Icon name="lock" size="sm" className="text-amber-500 shrink-0" />
      )}
      <span className="flex-1 truncate text-[10px] text-foreground" title={summary}>
        {summary}
      </span>
      {/* Row actions */}
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        <button onClick={() => onLock(variation.index)} title={variation.locked ? 'Unlock' : 'Lock'}>
          <Icon name={variation.locked ? 'lock' : 'lock-open'} size="sm" className="text-muted-foreground hover:text-foreground" />
        </button>
        <button onClick={() => onDuplicate(variation.index)} title="Duplicate">
          <Icon name="copy" size="sm" className="text-muted-foreground hover:text-foreground" />
        </button>
        <button onClick={() => onDelete(variation.index)} title="Delete">
          <Icon name="x" size="sm" className="text-muted-foreground hover:text-destructive" />
        </button>
      </div>
    </div>
  )
})

// ── Main component ──────────────────────────────────────────────────────────

function VariationGeneratorComponent({
  id,
  title,
  selected = false,
  width,
  height,
  onSelect,
  onHoverChange,
  onStateChange,
  state = {},
  connectedCategories,
  connectedConflictRules = [],
}: VariationGeneratorProps) {
  // Hydrate from persisted state
  const [count, setCount] = useState<number>(() => (state.count as number) ?? 1000)

  // Large data lives in external store (not React Flow node state)
  const variations = useSeriesDataStore(useCallback((s) => s.variations.get(id) ?? [], [id]))
  const storeSetVariations = useSeriesDataStore((s) => s.setVariations)
  const storeUpdateVariations = useSeriesDataStore((s) => s.updateVariations)

  const [genStatus, setGenStatus] = useState<'idle' | 'generating' | 'done'>(
    () => (state.generationStatus as 'idle' | 'generating' | 'done') ?? 'idle',
  )
  const [genStats, setGenStats] = useState<GenerationStats | null>(
    () => (state.generationStats as GenerationStats) ?? null,
  )
  const [progress, setProgress] = useState<{ generated: number; total: number } | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [seed, setSeed] = useState<number | undefined>(() => state.seed as number | undefined)

  // Virtual scroll state
  const [scrollTop, setScrollTop] = useState(0)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Persist changes to node state
  const isInitialMount = useRef(true)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    onStateChange?.((prev) => ({
      ...prev,
      count,
      seed,
      variationCount: variations.length,
      generationStatus: genStatus,
      generationStats: genStats,
    }))
  }, [count, seed, variations.length, genStatus, genStats, onStateChange])

  // ── Upstream data summary ─────────────────────────────────────────────

  const categoryCount = connectedCategories?.length ?? 0
  const optionCount = connectedCategories?.reduce((s, c) => s + c.options.length, 0) ?? 0
  const conflictRules = connectedConflictRules

  // ── Generate ──────────────────────────────────────────────────────────

  const isGenerating = useRef(false)

  const handleGenerate = useCallback(async () => {
    if (!connectedCategories?.length || isGenerating.current) return
    isGenerating.current = true
    setGenStatus('generating')
    setProgress({ generated: 0, total: count })

    const result = await generateVariationsAsync(
      connectedCategories,
      count,
      conflictRules,
      (generated, total) => setProgress({ generated, total }),
      seed,
    )

    storeSetVariations(id, result.variations)
    setGenStats(result.stats)
    setGenStatus('done')
    setProgress(null)
    isGenerating.current = false
  }, [connectedCategories, count, conflictRules, seed])

  // ── Row actions ────────────────────────────────────────────────────────

  const handleSwapAttribute = useCallback((variationIndex: number, categoryId: string, optionId: string) => {
    storeUpdateVariations(id, (prev) =>
      prev.map((v) => {
        if (v.index !== variationIndex) return v
        const cat = connectedCategories?.find((c) => c.id === categoryId)
        const opt = cat?.options.find((o) => o.id === optionId)
        if (!opt) return v
        return {
          ...v,
          status: 'manual' as const,
          attributes: v.attributes.map((a) =>
            a.categoryId === categoryId
              ? { ...a, optionId: opt.id, optionName: opt.name, promptFragment: opt.promptFragment }
              : a
          ),
        }
      })
    )
  }, [id, connectedCategories, storeUpdateVariations])

  const handleLock = useCallback((variationIndex: number) => {
    storeUpdateVariations(id, (prev) =>
      prev.map((v) => v.index === variationIndex ? { ...v, locked: !v.locked } : v)
    )
  }, [id, storeUpdateVariations])

  const handleDeleteVariation = useCallback((variationIndex: number) => {
    storeUpdateVariations(id, (prev) => {
      const filtered = prev.filter((v) => v.index !== variationIndex)
      return filtered.map((v, i) => ({ ...v, index: i + 1 }))
    })
  }, [id, storeUpdateVariations])

  const handleDuplicate = useCallback((variationIndex: number) => {
    storeUpdateVariations(id, (prev) => {
      const source = prev.find((v) => v.index === variationIndex)
      if (!source) return prev
      const dup: GeneratedVariation = {
        ...source,
        index: prev.length + 1,
        hash: `${source.hash}-dup-${Date.now()}`,
        locked: false,
        status: 'manual',
      }
      return [...prev, dup]
    })
  }, [id, storeUpdateVariations])

  // ── Count change ──────────────────────────────────────────────────────

  const handleCountChange = useCallback((val: number) => {
    setCount(Math.max(1, Math.min(10000, val)))
  }, [])

  // ── Filter ─────────────────────────────────────────────────────────────

  const filteredVariations = useMemo(() => {
    if (!searchQuery.trim()) return variations
    const q = searchQuery.toLowerCase()
    return variations.filter((v) =>
      v.attributes.some((a) =>
        a.optionName?.toLowerCase().includes(q) || a.categoryName?.toLowerCase().includes(q)
      )
    )
  }, [variations, searchQuery])

  // ── Virtual scroll ────────────────────────────────────────────────────

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop)
  }, [])

  const totalHeight = filteredVariations.length * ROW_HEIGHT
  const containerHeight = scrollRef.current?.clientHeight ?? 300

  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - BUFFER_ROWS)
  const endIndex = Math.min(
    filteredVariations.length,
    Math.ceil((scrollTop + containerHeight) / ROW_HEIGHT) + BUFFER_ROWS,
  )
  const visibleVariations = filteredVariations.slice(startIndex, endIndex)

  // ── Wheel scroll isolation ────────────────────────────────────────────

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const handleWheel = (e: WheelEvent) => {
      const canScrollDown = el.scrollTop < el.scrollHeight - el.clientHeight
      const canScrollUp = el.scrollTop > 0
      if ((e.deltaY > 0 && canScrollDown) || (e.deltaY < 0 && canScrollUp)) {
        e.stopPropagation()
      }
    }
    el.addEventListener('wheel', handleWheel)
    return () => el.removeEventListener('wheel', handleWheel)
  }, [])

  // ── Status text ───────────────────────────────────────────────────────

  const statusText = useMemo(() => {
    if (!connectedCategories?.length) return 'No data table connected'
    if (genStatus === 'generating' && progress) {
      return `Generating... ${progress.generated}/${progress.total}`
    }
    if (genStatus === 'done' && genStats) {
      const warn = genStats.generated < genStats.requested
        ? ` (max ${genStats.maxPossible} possible)`
        : ''
      const accuracy = genStats.maxDeviation !== undefined
        ? genStats.maxDeviation <= 2 ? ' · ✓ accurate' : ` · ${genStats.maxDeviation.toFixed(1)}% deviation`
        : ''
      return `${genStats.generated} variations · ${genStats.durationMs}ms${warn}${accuracy}`
    }
    const rulesInfo = conflictRules.length > 0 ? ` · ${conflictRules.length} rules` : ''
    return `Ready · ${categoryCount} categories · ${optionCount} options${rulesInfo}`
  }, [connectedCategories, genStatus, progress, genStats, categoryCount, optionCount, conflictRules])

  return (
    <NodeContainer
      nodeId={id}
      title={title || 'Variation Generator'}
      icon="shuffle"
      selected={selected}
      nodeType="text"
      width={width}
      onSelect={onSelect}
      onHoverChange={onHoverChange}
      onStateChange={onStateChange}
      state={state}
    >
      <CardContent className="relative flex flex-col flex-1 p-0 overflow-hidden">
        {/* Summary header */}
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/50">
          <span className="text-[11px] text-muted-foreground tabular-nums">
            {statusText}
          </span>
        </div>

        {/* Controls row */}
        <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-border/50">
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-[10px] text-muted-foreground">N</span>
            <input
              type="number"
              value={count}
              onChange={(e) => handleCountChange(Number(e.target.value) || 1)}
              min={1}
              max={10000}
              className="h-6 w-14 rounded border border-border/50 bg-muted px-1 text-xs text-center text-foreground outline-none nodrag nopan tabular-nums"
              disabled={genStatus === 'generating'}
            />
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-[10px] text-muted-foreground">Seed</span>
            <input
              type="number"
              value={seed ?? ''}
              onChange={(e) => setSeed(e.target.value ? Number(e.target.value) : undefined)}
              placeholder="rnd"
              className="h-6 w-12 rounded border border-border/50 bg-muted px-1 text-xs text-center text-foreground outline-none nodrag nopan placeholder:text-muted-foreground/50"
              disabled={genStatus === 'generating'}
            />
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={handleGenerate}
            disabled={!connectedCategories?.length || genStatus === 'generating'}
            className="h-6 gap-1 text-[10px] px-2 flex-1 shrink-0"
          >
            {genStatus === 'generating' ? (
              <Icon name="loader" size="sm" className="animate-spin" />
            ) : (
              <Icon name="shuffle" size="sm" />
            )}
            Generate
          </Button>
        </div>


        {/* Search (only when variations exist) */}
        {variations.length > 0 && (
          <div className="flex items-center gap-1.5 px-2 py-1 border-b border-border/50">
            <Icon name="search" size="sm" className="text-muted-foreground shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setScrollTop(0) }}
              placeholder="Filter variations..."
              className="flex-1 bg-transparent text-[11px] outline-none placeholder:text-muted-foreground/50 nodrag nopan"
            />
            {searchQuery && (
              <span className="text-[10px] text-muted-foreground tabular-nums">
                {filteredVariations.length}/{variations.length}
              </span>
            )}
          </div>
        )}

        {/* Variation list (virtual scroll) */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto scrollbar-thin"
          style={{ maxHeight: '380px' }}
          onScroll={handleScroll}
        >
          {variations.length > 0 ? (
            <div style={{ height: totalHeight, position: 'relative' }}>
              {visibleVariations.map((v, i) => (
                <VariationRow
                  key={v.hash}
                  variation={v}
                  categories={connectedCategories ?? null}
                  onSwapAttribute={handleSwapAttribute}
                  onLock={handleLock}
                  onDelete={handleDeleteVariation}
                  onDuplicate={handleDuplicate}
                  style={{
                    position: 'absolute',
                    top: (startIndex + i) * ROW_HEIGHT,
                    left: 0,
                    right: 0,
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8 text-xs text-muted-foreground">
              {connectedCategories?.length
                ? 'Click Generate to create variations'
                : 'Connect a Data Table to get started'}
            </div>
          )}
        </div>
      </CardContent>
    </NodeContainer>
  )
}

export const VariationGenerator = withNodeOptimization(VariationGeneratorComponent, {
  compareProps: ['id', 'title', 'selected', 'state', 'connectedCategories'],
})
